import {
  QuotesIcon,
  ExcludeSquareIcon,
  PackageIcon,
  BuildingsIcon,
  TrophyIcon,
  ProhibitIcon,
  ChatIcon,
  ChatSlashIcon,
  ChatsIcon,
  GridFourIcon,
  TextIndentIcon,
  FileMagnifyingGlassIcon,
  SparkleIcon,
  ScissorsIcon,
  type Icon,
} from '@phosphor-icons/react';

import { Env, getEnv } from 'lib/utils';

import { getExternalLinkLabel } from '../components/citation-card/CitationCard.utils';

import type {
  CitationData,
  CitationSource,
  CitationType,
  IndexerType,
} from '../types';

const CITATION_TYPE_ICONS: Partial<Record<CitationType, Icon>> = {
  comparison: ExcludeSquareIcon,
  evidence: FileMagnifyingGlassIcon,
  product: PackageIcon,
  company: BuildingsIcon,
  winstory: TrophyIcon,
  lossstory: ProhibitIcon,
  prospectquote: ChatIcon,
  objectionhandling: ChatSlashIcon,
  talktracks: ChatsIcon,
  collection: GridFourIcon,
  quote: QuotesIcon,
  insight: SparkleIcon,
  snippet: ScissorsIcon,
};

export function getCitationTypeIcon(type: CitationType): Icon {
  return CITATION_TYPE_ICONS[type] ?? TextIndentIcon;
}

/** Link text the backend uses for citation links: `[source](url)`. */
export const CITATION_LINK_TEXT = 'source';

export const CITATION_TEXT_PATTERN = /\[source\]\(([^)[\]]+)\)/g;

/** `source_name` for a citation that points at a public website. */
export const WEB_CITATION_SOURCE_NAME = 'web';

/**
 * Builds a citation for a public web page. There is no indexer metadata for
 * such a URL, so the domain stands in as the title and the page itself is the
 * only external link.
 */
export function createWebCitationSource(url: string): CitationSource {
  return {
    url,
    citation_data: {
      title: getExternalLinkLabel(url),
      sources: [{ url }],
      source_name: WEB_CITATION_SOURCE_NAME,
    },
  };
}

export const INDEXER_TYPE_TO_CITATION_TYPE: Record<IndexerType, CitationType> =
  {
    'curated-snippets': 'snippet',
    'evidence-quotes': 'quote',
    'markdown-source': 'document',
    'stak-cards': 'insight',
  } as const;

const SOURCE_TYPE_ACTION_LABELS: Record<IndexerType, string> = {
  'curated-snippets': 'Edit Snippet',
  'evidence-quotes': '',
  'markdown-source': '',
  'stak-cards': 'View Auto Insight',
} as const;

export function getSourceTypeActionLabel(indexerType: IndexerType): string {
  return SOURCE_TYPE_ACTION_LABELS[indexerType] ?? 'View source';
}

const CITATION_HOSTNAMES = [
  'indexer-api-service\\.indexer\\.svc\\.cluster\\.local',
  'athena-api\\.athena\\.svc\\.cluster\\.local',
];

const DEV_CITATION_HOSTNAMES = [
  'host\\.docker\\.internal:\\d+',
  'localhost:\\d+',
];

function buildCitationUrlPattern(isDev: boolean): RegExp {
  const hosts = [
    ...CITATION_HOSTNAMES,
    ...(isDev ? DEV_CITATION_HOSTNAMES : []),
  ].join('|');
  return new RegExp(
    `^https?://(?:${hosts})(?:/indexer)?/([a-z][a-z0-9-]*)/([\\w-]+)(?:/.*)?$`,
  );
}

export const CITATION_URL_PATTERN = buildCitationUrlPattern(
  import.meta.env.DEV || import.meta.env.STORYBOOK,
);

export function parseIndexerUrl(url: string): {
  indexerType?: IndexerType;
  id?: string;
  type: CitationType;
} {
  const match = CITATION_URL_PATTERN.exec(url);

  if (!match) {
    return { type: 'url' };
  }

  const [, indexerTypeStr, id] = match;
  const indexerType = indexerTypeStr as IndexerType;
  const type = INDEXER_TYPE_TO_CITATION_TYPE[indexerType] ?? 'document';

  return { indexerType, id, type };
}

export function isCitationSourceFetched(
  source: CitationSource | { url: string },
): boolean {
  return 'citation_data' in source;
}

export function isCitationSourceEmpty(
  source: CitationSource | { url: string },
): boolean {
  if (!('citation_data' in source)) return false;

  return (
    !source.citation_data.title &&
    !source.citation_data.description &&
    (!source.citation_data.sources || source.citation_data.sources.length === 0)
  );
}

export const convertTextWithCitations = (text: string) => {
  let match;
  let remainingText = text;
  const textChunks: string[] = [];
  const knownSources = new Map<string, number>();
  let citationNum = 0;

  while ((match = CITATION_TEXT_PATTERN.exec(remainingText))) {
    textChunks.push(remainingText.slice(0, match.index));
    const citationUrl = match[1];
    const citationId = knownSources.get(citationUrl);

    if (citationId !== undefined) {
      textChunks.push(`[[${citationId}]](${citationUrl})`);
    } else {
      citationNum++;
      knownSources.set(citationUrl, citationNum);
      textChunks.push(`[[${citationNum}]](${citationUrl})`);
    }

    remainingText = remainingText.slice(match.index + match[0].length);
  }

  if (remainingText) {
    textChunks.push(remainingText);
  }

  return textChunks.join('');
};

export function isInternalUrl(url: string, internalUrlBase?: string): boolean {
  try {
    const parsedUrl = parseIndexerUrl(url);
    if (parsedUrl.indexerType === 'curated-snippets') {
      return true;
    }

    if (!internalUrlBase) return false;

    const urlObj = new URL(url, window.location.origin);
    const isProd = getEnv() === Env.Production;
    const isCorrectOrigin = !isProd || urlObj.origin === window.location.origin;
    return isCorrectOrigin && urlObj.pathname.startsWith(internalUrlBase);
  } catch {
    return false;
  }
}

function getCitationTypeReadableName(type: CitationType): string {
  const CITATION_TYPE_NAMES: Record<CitationType, string> = {
    comparison: 'Comparison',
    evidence: 'Evidence',
    product: 'Product',
    company: 'Company',
    winstory: 'Win Story',
    lossstory: 'Loss Story',
    prospectquote: 'Prospect Quote',
    objectionhandling: 'Objection Handling',
    talktracks: 'Talk Tracks',
    collection: 'Collection',
    quote: 'Quote',
    excerpt: 'Excerpt',
    document: 'Document',
    url: 'URL',
    insight: 'Insight',
    snippet: 'Curated Snippet',
  } as const;
  return CITATION_TYPE_NAMES[type] ?? 'Source';
}

export function getEffectiveCitationTitle(
  source: CitationSource | { url: string },
  fetched?: CitationData,
) {
  const parsedUrl = parseIndexerUrl(source.url);
  return (
    ('citation_data' in source && source.citation_data.title) ||
    fetched?.title ||
    getCitationTypeReadableName(parsedUrl.type)
  );
}

export function getEffectiveCitationDescription(
  source: CitationSource | { url: string },
  fetched?: CitationData,
) {
  return (
    ('citation_data' in source && source.citation_data.description) ||
    fetched?.description
  );
}

export function getEffectiveCitationSources(
  source: CitationSource | { url: string },
  fetched?: CitationData,
) {
  return (
    ('citation_data' in source && source.citation_data.sources) ||
    fetched?.sources ||
    []
  );
}

/**
 * Deduplicates fully resolved citations by URL.
 * Use this when loading saved messages/threads from backend.
 * Do NOT use during streaming - sparse arrays should be passed through as-is.
 */
export function dedupeSourcesByUrl(
  sources: CitationSource[],
): CitationSource[] {
  const seen = new Set<string>();
  return sources.filter((source) => {
    if (source) {
      if (seen.has(source.url)) {
        return false;
      }
      seen.add(source.url);
    }
    return true;
  });
}

export const CITATIONS_WITH_MARKDOWN_DESCRIPTIONS = [
  'markdown-source',
  'stak-cards',
];

/**
 * Extracts the first public URL from a citation source's citation_data.sources.
 * Returns undefined if no public URL is available.
 */
export function getPublicCitationUrl(
  source: CitationSource | { url: string },
): string | undefined {
  if ('citation_data' in source) {
    const sources = source.citation_data.sources;
    if (sources?.length && sources[0].url) {
      return sources[0].url;
    }
  }
  return undefined;
}

export const FOOTNOTE_CITATION_PATTERN = /\[\^(\d+)\]/g;

// Matches any [^<content>] citation marker. [^\]]+ is a negated character
// class that explicitly excludes the closing \] anchor, so no character can
// satisfy both the repeated class and the terminator — backtracking is
// impossible. The static analyser cannot prove this, hence the suppression.
// URL citations are distinguished from numbered ones via .includes('://').
// eslint-disable-next-line redos/no-vulnerable
export const URL_CITATION_PATTERN = /\[\^([^\]]+)\]/;

/**
 * Transforms [^https://url] URL-based citations in content to numbered [^n] format.
 * - Numbers are assigned in first-appearance order (1-based, deduplicated)
 * - Citations whose URL has no matching entry in citationSources are removed (replaced with empty string)
 * - Returns reordered citationSources aligned with the new numbering (index 0 = citation 1)
 */
export function transformUrlCitationsToNumbered(
  content: string,
  citationSources: CitationSource[],
): { content: string; citationSources: CitationSource[] } {
  const sourceByUrl = new Map<string, CitationSource>(
    citationSources.map((s) => [s.url, s]),
  );

  const urlToNumber = new Map<string, number>();
  const orderedSources: CitationSource[] = [];

  // A single pattern instance shared across both passes.
  // matchAll clones the regex internally (per spec), so pattern.lastIndex stays 0
  // after pass 1 and can be reused directly for the pass 2 replace.
  const pattern = new RegExp(URL_CITATION_PATTERN.source, 'g');

  // Pass 1: assign numbers in first-appearance order, skip non-URL or unmatched citations
  for (const match of content.matchAll(pattern)) {
    const url = match[1];
    if (!url.includes('://')) continue;
    if (!urlToNumber.has(url) && sourceByUrl.has(url)) {
      urlToNumber.set(url, urlToNumber.size + 1);
      const source = sourceByUrl.get(url);
      if (source !== undefined) {
        orderedSources.push(source);
      }
    }
  }

  // Pass 2: replace [^url] with [^n], remove unmatched URL citations, leave others untouched
  const transformedContent = content.replace(
    pattern,
    (fullMatch, url: string) => {
      if (!url.includes('://')) return fullMatch;
      const num = urlToNumber.get(url);
      return num !== undefined ? `[^${num}]` : '';
    },
  );

  return { content: transformedContent, citationSources: orderedSources };
}

/**
 * Transforms numbered citations back to URL-based citations for backend storage.
 *
 * Inverse of `transformUrlCitationsToNumbered`:
 * - [^1] → [^https://first-source-url.com]
 * - [^2] → [^https://second-source-url.com]
 *
 * Citations referencing an index beyond the sources array are left unchanged.
 * The sources array is 0-indexed (citation [^1] maps to sources[0]).
 */
export function transformNumberedCitationsToUrl(
  content: string,
  citationSources: CitationSource[],
): string {
  return content.replace(/\[\^(\d+)\]/g, (match, num: string) => {
    const source = citationSources[parseInt(num, 10) - 1];
    return source?.url ? `[^${source.url}]` : match;
  });
}

/**
 * Resolves [^N] footnote citations to [N](url) markdown links using the sources array.
 * Citations without public URLs are removed entirely.
 * Citations are renumbered sequentially (1, 2, 3...) based on which ones have URLs.
 * Used when converting research mode answers to card HTML.
 */
export function resolveFootnoteCitationsToUrls(
  text: string,
  sources: CitationSource[],
): string {
  // Pass 1: Build renumber map — only citations with public URLs get a new number
  const renumberMap = new Map<number, { newNumber: number; url: string }>();
  let nextNumber = 1;
  let match: RegExpExecArray | null;

  while ((match = FOOTNOTE_CITATION_PATTERN.exec(text)) !== null) {
    const originalIndex = parseInt(match[1], 10) - 1;
    if (renumberMap.has(originalIndex)) continue;

    const source = sources[originalIndex];
    if (source) {
      const publicUrl = getPublicCitationUrl(source);
      if (publicUrl) {
        renumberMap.set(originalIndex, {
          newNumber: nextNumber++,
          url: publicUrl,
        });
      }
    }
  }

  // Pass 2: Replace — citations with URLs get renumbered, others are removed
  return text.replace(FOOTNOTE_CITATION_PATTERN, (_, numStr: string) => {
    const originalIndex = parseInt(numStr, 10) - 1;
    const entry = renumberMap.get(originalIndex);
    if (entry) {
      return `[${entry.newNumber}](${entry.url})`;
    }
    return '';
  });
}
